import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { PhArrowsClockwise, PhCloudArrowUp } from 'ember-phosphor-icons';
import Button from 'tio-reimagined/components/button';
import ReSelect from 'tio-reimagined/components/select';
import FileUploader from '../../../file-uploader.gts';
import { DrawerHeader, DrawerBody } from 'tio-reimagined/components/drawer';
import AttachmentsAnalysisView from '../../approval/drawers/ai-analysis/attachments-analysis-view.gts';
import AttachmentList from '../../../tuition-assistance/forms/upload-attachments/attachment-list.gts';
import type TasProgramInstanceModel from '../../../../models/tas-program-instance.ts';
import type TasAssetModel from '../../../../models/tas-asset.ts';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type StoreService from '@ember-data/store';

const CLASSIFICATION_OPTIONS = [
  { label: 'Program Approval Documents', key: 'PROGRAM_APPROVAL_DOCUMENTS' },
  { label: 'Program Completion Documents', key: 'PROGRAM_COMPLETION_DOCUMENTS' },
];

interface ProgramAttachmentsDrawerSignature {
  Args: {
    instance: TasProgramInstanceModel;
    allAttachments: TasAssetModel[];
    disabled?: boolean;
    hideDiscarded?: boolean;
  };
}

export default class ProgramAttachmentsDrawer extends Component<ProgramAttachmentsDrawerSignature> {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;

  @tracked showUploadSection = false;
  @tracked selectedClassification = 'PROGRAM_APPROVAL_DOCUMENTS';

  get sortedAttachments(): TasAssetModel[] {
    const assets = this.args.allAttachments ?? [];
    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (this.args.hideDiscarded) {
      return notDeleted;
    }

    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);
    return [...notDeleted, ...deleted];
  }

  get assetSummaries() {
    return this.args.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary) => summary !== null);
  }

  get canUpload(): boolean {
    return !this.args.disabled;
  }

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
  };

  classificationDidChange = (key: string) => {
    this.selectedClassification = key;
  };

  isDeleted = (asset: TasAssetModel): boolean => {
    return asset.attachmentDeletedAt !== null;
  };

  uploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);
    asset.classification = this.selectedClassification;

    const instance = this.args.instance;

    // @ts-expect-error - Dynamic relationship access
    asset.tasProgramInstances.push(instance);
    // @ts-expect-error - Dynamic relationship access
    instance.tasAssets.push(asset);

    await asset.save();
    await instance.save();

    this.showUploadSection = false;
  });

  deleteAttachment = enqueueTask(async (asset: TasAssetModel) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      return;
    }
    await attachment.destroyRecord();
    await asset.reload();
  });

  <template>
    <DrawerHeader>
      <h2 class="text-lg font-semibold text-gray-800">
        {{t "common.tas.approval.drawer.attachments.title" default="Attachments"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="flex flex-col gap-6 p-6">
        {{! Upload Button }}
        {{#if this.canUpload}}
          {{#unless this.showUploadSection}}
            <Button
              @intent="primary"
              @appearance="outlined"
              @icon={{PhCloudArrowUp}}
              @label={{t
                "common.tas.approval.drawer.attachments.upload_attachment"
                default="Upload Attachment"
              }}
              class="w-full"
              {{on "click" this.toggleUploadSection}}
              data-test-upload-attachment-button
            />
          {{/unless}}
        {{/if}}

        {{! Upload Section }}
        {{#if this.showUploadSection}}
          <div class="border border-blue-200 rounded-lg bg-white shadow-sm">
            <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div class="flex items-center gap-2">
                <PhCloudArrowUp @size="20" class="text-blue-600" />
                <h3 class="text-base font-semibold text-gray-900">
                  {{t "tuition_assistance.program_details.upload_attachments"}}
                </h3>
              </div>
              <Button
                @appearance="minimal"
                @size="sm"
                @intent="default"
                {{on "click" this.toggleUploadSection}}
                data-test-cancel-upload-button
              >
                {{t "common.cancel"}}
              </Button>
            </div>
            <div class="px-6 py-5 space-y-5">
              <div>
                <label class="text-sm font-medium text-gray-700 block mb-2">
                  {{t
                    "tuition_assistance.program_details.courses.document_type"
                    default="Document Type"
                  }}
                </label>
                <ReSelect
                  @items={{CLASSIFICATION_OPTIONS}}
                  @selected={{this.selectedClassification}}
                  @onChange={{this.classificationDidChange}}
                  @clearable={{false}}
                />
              </div>
              <div>
                <FileUploader @didUploadFile={{this.uploadFile.perform}} as |uploader|>
                  <input
                    id={{uploader.uniqueId}}
                    type="file"
                    multiple="true"
                    {{uploader.queue.selectFile}}
                    class="hidden"
                    disabled={{this.uploadFile.isRunning}}
                  />
                  <label
                    for={{uploader.uniqueId}}
                    class="group block cursor-pointer rounded-lg border-2 border-dashed transition-all
                      {{if
                        this.uploadFile.isRunning
                        'border-gray-300 bg-gray-50 cursor-not-allowed'
                        'border-gray-300 hover:border-blue-400 bg-gray-50 hover:bg-blue-50'
                      }}"
                  >
                    <div class="px-6 py-8 text-center">
                      {{#if this.uploadFile.isRunning}}
                        <PhArrowsClockwise
                          @size="32"
                          class="mx-auto mb-3 text-blue-600 animate-spin"
                        />
                        <p class="text-sm font-medium text-gray-600">
                          Uploading...
                        </p>
                      {{else}}
                        <PhCloudArrowUp
                          @size="32"
                          class="mx-auto mb-3 text-gray-400 group-hover:text-blue-600 transition-colors"
                        />
                        <div class="flex items-center justify-center gap-2 mb-2">
                          <span
                            class="text-sm font-semibold text-blue-600 group-hover:text-blue-700"
                          >
                            {{t "choose_file" default="Choose File"}}
                          </span>
                          <span class="text-sm text-gray-500">
                            or drag and drop
                          </span>
                        </div>
                        <p class="text-xs text-gray-500">
                          You can upload multiple files
                        </p>
                      {{/if}}
                    </div>
                  </label>
                </FileUploader>
              </div>
            </div>
          </div>
        {{/if}}

        {{! Attachments View }}
        {{#if @hideDiscarded}}
          <AttachmentList
            @assets={{this.sortedAttachments}}
            @disabled={{@disabled}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <AttachmentsAnalysisView
            @attachments={{this.sortedAttachments}}
            @assetSummaries={{this.assetSummaries}}
            @deleteAttachment={{this.deleteAttachment}}
            @isDeleted={{this.isDeleted}}
          />
        {{/if}}
      </div>
    </DrawerBody>
  </template>
}
